import { useEffect, useState } from 'react';

import { websocketService } from '../services/websocketService';
import type { ObserverState } from '../services/websocketService';

/**
 * "Is there a television in this room?"
 *
 * The answer changes what a phone IS. With no TV, the phone is the whole game:
 * the stage, the clock, the tally, the drama. With a TV, the phone is a
 * CONTROLLER — four inputs and a button — and the drama happens on the wall
 * where everybody can see it at once.
 *
 * That is the entire point of TV mode: **everyone looks up from their phone.**
 *
 * Driven by the server's `observer_joined` / `observer_left` broadcasts, plus a
 * `getObserverState` on mount for the phone that walks into a room where the TV
 * is already on. It reverses instantly when the TV is switched off — a phone
 * stuck in controller mode with no television in front of it is a phone with no
 * game on it.
 */
export function useTvConnected(roomCode: string | null | undefined): boolean {
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!roomCode) {
      setConnected(false);
      return;
    }

    const onJoined = (s: ObserverState) => {
      if (!s || s.roomCode !== roomCode.toUpperCase()) return;
      setConnected(!!s.tvConnected);
    };
    const onLeft = onJoined; // same payload, same truth: `tvConnected`

    websocketService.on('observer_joined', onJoined);
    websocketService.on('observer_left', onLeft);
    websocketService.requestObserverState(roomCode);

    return () => {
      websocketService.off('observer_joined', onJoined);
      websocketService.off('observer_left', onLeft);
    };
  }, [roomCode]);

  return connected;
}
